import React, { useEffect, useState, useRef } from "react";

// core components
import { makeStyles } from "@material-ui/core/styles";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Button, Box, Paper, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { CheckCircleOutline as CheckCircleOutlineIcon } from "components/MaterialSymbol/MaterialIcons";
//libs
import { useFormik } from "formik";
import * as yup from "yup";
import useUser from "hooks/useUser";

//services
import { financial } from "helpers";
import PaymentMethods from "components/PaymentMethods/PaymentMethods";
import ActionAlerts, { AlertSeverity } from "components/ActionAlerts/ActionAlerts";
import usePaymentHandler from "hooks/usePaymentHandler";
import usePaymentGateway from "hooks/usePaymentGateway";

const validationSchema = {
    amountToBeCharged: yup.number().required().min(1),
    reason: yup.string().required(),
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        alignItems: "center",
    },
    wrapper: {
        margin: theme.spacing(1),
        position: "relative",
    },
    heading: {
        color: "#18324b",
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: "#526a82",
        marginBottom: theme.spacing(3),
    },
    paymentAlert: {
        alignItems: "flex-start",
        backgroundColor: "#edf7f1",
        borderRadius: 9,
        color: "#147b4f",
        marginBottom: theme.spacing(3),
        padding: theme.spacing(1.5, 2),
        "& .MuiAlert-icon": {
            color: "#147b4f",
            marginTop: 2,
        },
        "& .MuiAlert-icon svg": {
            border: "1px solid #147b4f",
            borderRadius: "50%",
            boxSizing: "border-box",
            padding: 2,
        },
        "& .MuiAlert-message": {
            padding: 0,
        },
    },
    paymentAlertLine: {
        display: "block",
        fontSize: 14,
        lineHeight: 1.6,
    },
    chargeCard: {
        backgroundColor: "#fff7ef",
        border: "1px solid #f2c8a8",
        borderRadius: 10,
        color: "#18324b",
        marginBottom: theme.spacing(2.5),
        padding: theme.spacing(2.5),
    },
    chargeEyebrow: {
        color: "#c94b1c",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.08em",
        marginBottom: theme.spacing(1),
    },
    chargeAmount: {
        fontSize: 44,
        fontWeight: 400,
        letterSpacing: "-0.03em",
        lineHeight: 1,
    },
    chargeCurrency: {
        color: "#526a82",
        fontSize: 14,
        marginLeft: theme.spacing(1),
    },
    chargeCopy: {
        color: "#526a82",
        fontSize: 14,
        marginTop: theme.spacing(1.5),
    },
    reviewStatement: {
        color: "#18324b",
        fontSize: 14,
        lineHeight: 1.6,
        marginBottom: theme.spacing(1),
    },
    footer: {
        alignItems: "center",
        borderTop: "1px solid #e2e7eb",
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(3),
        paddingTop: theme.spacing(2),
    },
    cancelButton: {
        borderColor: "#d9dee5 !important",
        color: "#18324b !important",
    },
    primaryButton: {
        backgroundColor: "#c94b1c !important",
        color: "#fff !important",
        "&:hover": {
            backgroundColor: "#a93d16 !important",
        },
    },
    inlineProgress: {
        color: "inherit",
        marginRight: theme.spacing(1),
    },
    successState: {
        padding: theme.spacing(2, 1, 1),
        textAlign: "center",
    },
    successAlert: {
        alignItems: "center",
        backgroundColor: "#edf7f1",
        borderRadius: 10,
        color: "#147b4f",
        display: "flex",
        justifyContent: "center",
        margin: theme.spacing(2, 0, 1),
        padding: theme.spacing(2),
        "& .MuiAlert-message": {
            padding: 0,
        },
    },
    successMessage: {
        fontSize: 17,
        fontWeight: 600,
        lineHeight: 1.55,
    },
    successIcon: {
        color: "#147b4f",
        fontSize: 56,
        margin: theme.spacing(1, 0, 1.5),
    },
    successCountdown: {
        color: "#526a82",
        fontSize: 14,
    },
    fabProgress: {
        position: "absolute",
        top: -6,
        left: -6,
        zIndex: 1,
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));


const PaidOrderNotice = ({ classes }) => (
    <Alert
        severity="success"
        icon={<CheckCircleOutlineIcon />}
        className={classes.paymentAlert}
    >
        <Typography className={classes.paymentAlertLine}>
            The original order has already been paid.
        </Typography>
        <Typography className={classes.paymentAlertLine}>
            Payment was collected at checkout.
        </Typography>
    </Alert>
);

const ChargeAmountCard = ({ amount, classes }) => (
    <Paper elevation={0} className={classes.chargeCard}>
        <Typography className={classes.chargeEyebrow}>EXTRA CHARGE</Typography>
        <Box display="flex" alignItems="baseline">
            <Typography component="span" className={classes.chargeAmount}>
                ${financial(amount)}
            </Typography>
            <Typography component="span" className={classes.chargeCurrency}>
                USD
            </Typography>
        </Box>
        <Typography className={classes.chargeCopy}>
            Charged in addition to the original payment.
        </Typography>
    </Paper>
);

export default function CCchargeForm({
    dealerId,
    customerId,
    orderId,
    orderNumber,
    fullAmount = 0,
    pgatewayId = null,
    billingInfo = {},
    onSuccess = () => {},
    onError = () => {},
    onCancel = () => {},
}) {
    const classes = useStyles();
    const [user] = useUser();
    const { loading, postPaypalPro, postAuthorizeNet, postStripe } = usePaymentHandler();
    const { resolvedGatewayId, errorMessage } = usePaymentGateway(customerId, pgatewayId);
    const isUnsupportedAdditionalCharge = /does not support additional charges/i.test(
        errorMessage || "",
    );
    const [isReviewing, setIsReviewing] = useState(false);
    const [reviewValues, setReviewValues] = useState(null);
    const [successAmount, setSuccessAmount] = useState(null);
    const [closeCountdown, setCloseCountdown] = useState(3);
    const submissionStartedRef = useRef(false);

    useEffect(() => {
        if (successAmount === null) {
            return undefined;
        }

        setCloseCountdown(3);
        const timer = setInterval(() => {
            setCloseCountdown((currentCountdown) => {
                if (currentCountdown <= 1) {
                    clearInterval(timer);
                    onCancel();
                    return 0;
                }

                return currentCountdown - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [successAmount, onCancel]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            dealerId,
            customerId,
            orderId,
            orderNumber,
            userId: user?.id || 0,
            fullAmount: Number(fullAmount),
            amountToBeCharged: 0,
            reason: "",
        },
        validationSchema: yup.object(validationSchema),
        onSubmit: (values) => {
            setReviewValues(values);
            setIsReviewing(true);
        },
    });

    const handleFinalConfirmation = async () => {
        if (!reviewValues || loading || submissionStartedRef.current) {
            return;
        }

        submissionStartedRef.current = true;
        const bodyParsed = {
            dealerId: reviewValues.dealerId,
            cId: reviewValues.customerId,
            transactionAmount: reviewValues.amountToBeCharged,
        };
        const handleSuccess = () => {
            setSuccessAmount(reviewValues.amountToBeCharged);
        };

        try {
            switch (resolvedGatewayId) {
                case "paypal_pro": // PayPal Pro
                    await postPaypalPro(bodyParsed, handleSuccess, onError);
                    break;
                case "authorize_net": // Authorize.Net
                    await postAuthorizeNet(bodyParsed, handleSuccess, onError);
                    break;
                case "stripe": // Stripe
                    await postStripe(bodyParsed, handleSuccess, onError);
                    break;
                default:
                    onError("No payment gateway selected or valid payment info found.");
            }
        } finally {
            submissionStartedRef.current = false;
        }
    };



    if (successAmount !== null) {
        const countdownLabel = closeCountdown === 1 ? "second" : "seconds";

        return (
            <Box className={classes.successState}>
                <Typography variant="h4" className={classes.heading}>
                    Extra charge processed
                </Typography>
                <Alert severity="success" icon={false} className={classes.successAlert}>
                    <Typography className={classes.successMessage}>
                        {`The extra charge has been successfully processed. The amount of $${financial(
                            successAmount,
                        )} has been charged to the customer's account.`}
                    </Typography>
                </Alert>
                <CheckCircleOutlineIcon className={classes.successIcon} />
                <Typography className={classes.successCountdown}>
                    Nothing else to do. This window will close in {closeCountdown} {countdownLabel}.
                </Typography>
            </Box>
        );
    }

    if (!resolvedGatewayId && errorMessage) {
        return (
            <Box>
                <Typography variant="h4" className={classes.heading}>
                    Confirm extra charge
                </Typography>
                <Typography className={classes.subtitle}>
                    Review the additional amount before continuing.
                </Typography>
                {!isUnsupportedAdditionalCharge && <PaidOrderNotice classes={classes} />}
                <ActionAlerts
                    message={errorMessage}
                    severity={
                        isUnsupportedAdditionalCharge
                            ? AlertSeverity.WARNING
                            : AlertSeverity.ERROR
                    }
                    autoCloseTime={null}
                    onClose={() => {}}
                />
                <Box className={classes.footer}>
                    <Button
                        type="button"
                        variant="outlined"
                        className={classes.cancelButton}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            {!isReviewing ? (
                <form onSubmit={formik.handleSubmit}>
                    <Box>
                        <Typography variant="h4" className={classes.heading}>
                            Confirm extra charge
                        </Typography>
                        <Typography className={classes.subtitle}>
                            Review the additional amount before continuing.
                        </Typography>
                        <PaidOrderNotice classes={classes} />
                        {resolvedGatewayId && (
                            <Typography variant="body2" className={classes.subtitle}>
                                This is a separate additional charge processed using the customer's
                                saved payment method.
                            </Typography>
                        )}
                    </Box>

                    <GridContainer>
                        <GridItem xs={12} sm={12} md={12}>
                            <TextField
                                type="number"
                                fullWidth
                                margin="dense"
                                id="amountToBeCharged"
                                name="amountToBeCharged"
                                label="Additional charge amount"
                                value={formik.values.amountToBeCharged}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.amountToBeCharged &&
                                    Boolean(formik.errors.amountToBeCharged)
                                }
                            />
                        </GridItem>
                        <GridItem xs={12} sm={12} md={12}>
                            <TextField
                                fullWidth
                                margin="dense"
                                id="reason"
                                name="reason"
                                label="Reason for additional charge"
                                multiline
                                rows={4}
                                value={formik.values.reason}
                                onChange={formik.handleChange}
                                error={formik.touched.reason && Boolean(formik.errors.reason)}
                                helperText="Required to document this extra charge."
                                inputProps={{
                                    maxLength: 255,
                                }}
                            />
                        </GridItem>
                    </GridContainer>

                    <Box className={classes.footer}>
                        <Button
                            type="button"
                            variant="outlined"
                            className={classes.cancelButton}
                            onClick={onCancel}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        {resolvedGatewayId && (
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                className={classes.primaryButton}
                                disabled={loading}
                            >
                                Review extra charge
                            </Button>
                        )}
                    </Box>
                </form>
            ) : (
                <Box>
                    <Typography variant="h4" className={classes.heading}>
                        Confirm extra charge
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Review the additional amount before continuing.
                    </Typography>
                    <PaidOrderNotice classes={classes} />
                    <ChargeAmountCard amount={reviewValues.amountToBeCharged} classes={classes} />
                    <Typography className={classes.reviewStatement}>
                        {`Continuing will charge an extra $${financial(
                            reviewValues.amountToBeCharged,
                        )} to the customer's saved payment method. This is separate from the original payment.`}
                    </Typography>
                    <Typography className={classes.reviewStatement}>
                        Confirm that the customer has authorized this additional charge before
                        proceeding.
                    </Typography>
                    <Box className={classes.footer}>
                        <Button
                            type="button"
                            variant="outlined"
                            className={classes.cancelButton}
                            onClick={() => setIsReviewing(false)}
                            disabled={loading}
                        >
                            Back
                        </Button>
                        <div className={classes.wrapper}>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                className={classes.primaryButton}
                                onClick={handleFinalConfirmation}
                                disabled={loading || submissionStartedRef.current}
                            >
                                {loading && (
                                    <CircularProgress size={18} className={classes.inlineProgress} />
                                )}
                                {loading
                                    ? "Processing extra charge..."
                                    : `Confirm extra charge — $${financial(
                                          reviewValues.amountToBeCharged
                                      )}`}
                            </Button>
                        </div>
                    </Box>
                </Box>
            )}

            {!resolvedGatewayId && (
                <Box style={{ marginTop: 10 }}>
                    <PaymentMethods
                        dealerId={dealerId}
                        amountToBeCharged={formik.values.amountToBeCharged}
                        billingInfo={billingInfo}
                        onSuccess={onSuccess}
                    />
                </Box>
            )}
        </>
    );
}
